import { useNavigate } from 'react-router-dom';
import { PhoneIncoming } from 'lucide-react';
import { outcomeLabel, type SweeperCallbackRow } from '@/domain/sweeperOutcomes';
import { relativeFromIso } from '@/utils/format';
import { formatStudioDay } from '@/utils/studioDay';

/**
 * The Sweeper's Callbacks band (B7, 75nh D08-26): clients she called who didn't
 * answer or asked to be called back. Their callback rings her, not the Agent,
 * for 7 days from her call — even after the gap fills, so she can offer them the
 * next slot. Like Missed deposits it takes priority, so it sits above the tabs.
 */
export default function SweeperCallbacks({ rows }: { rows: SweeperCallbackRow[] }) {
  const navigate = useNavigate();
  if (rows.length === 0) return null;
  return (
    <section className="mt-6 max-w-6xl panel-dark p-4" data-testid="sweeper-callbacks">
      <p className="label-small-caps text-gold-bright flex items-center gap-2">
        <PhoneIncoming size={12} /> Callbacks for you
        <span className="rounded-full bg-ink-soft px-1.5 py-px font-mono text-[10px] tabular-nums text-cream-soft">{rows.length}</span>
      </p>
      <p className="font-serif text-[12px] text-cream-faint mt-1">
        You called these clients. If they ring back, the call comes to you.
      </p>
      <ul className="mt-2">
        {rows.map((row) => {
          const c = row.customer;
          const name = c.preferredName ?? `${c.firstName} ${c.lastName}`;
          return (
            <li
              key={c.id}
              className="flex items-center gap-4 border-t border-gold-dim/15 py-2.5 first:border-t-0"
              data-testid={`sweeper-callback-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-inter text-[13px] text-cream font-semibold truncate">{name}</p>
                <p className="font-serif text-[12px] text-cream-soft">
                  {outcomeLabel(row.outcome.outcome)} · {relativeFromIso(row.outcome.at)} · routed to you until{' '}
                  {formatStudioDay(row.routedUntilIso, 'EEE d MMM')}
                  {row.outcome.note ? ` · ${row.outcome.note}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/customer/${c.id}`)}
                className="shrink-0 rounded-md border border-gold-dim/40 px-3 py-1.5 text-xs text-cream-soft hover:border-gold-bright hover:text-cream"
                data-testid={`sweeper-callback-open-${c.id}`}
              >
                Open record
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
